import { Component, OnInit, ViewChild, ElementRef, DestroyRef } from '@angular/core';
import {
  StepType,
  RegistrationStepQuery,
  RegistrationStepService,
  LoggingService,
  ErrorStateService,
  MemberDetails,
  ProviderUiActionResponse,
  MemberDetailsRegistration,
  PaymentService,
  GiftCodeModel,
  GiftCodeType,
  GiftCodeEntitlementValueType,
  RegistrationPurchaseBasket,
  RegistrationBasketService,
  RegistrationBasketQuery,
  GiftCodeService,
  ProviderActionKind,
  MemberQuery,
  AddressQuery,
  AddressType,
  Address,
  RegistrationPurchaseBasketItem,
  CardTokenQuery,
  AuthService,
  AddressService,
  Country,
  AccountStatus,
  RegistrationBasketState,
  MemberService,
  ApiService,
  Region,
  toNumber,
} from '@fgb/core';
import { Observable, Subject, firstValueFrom, iif, lastValueFrom, of } from 'rxjs';
import { UntypedFormGroup, UntypedFormBuilder, Validators, FormControl } from '@angular/forms';
import { switchMap, tap } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FGBValidators } from '@fgb/portal-component-library/src/lib/shared/validators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

class RegistrationPurchaseBasketExtended implements RegistrationBasketState {
  BasketItems: RegistrationPurchaseBasketItem[] = [];
  GiftCodes: GiftCodeModel[] = [];
  Tax: number = 0;
  TotalTax: number = 0;
  TotalPrice: number = 0;
  TotalShipping: number = 0;
  TotalPriceWithTax: number = 0;
  TotalPriceWithTaxAndShipping: number = 0;
  constructor(o?: Partial<RegistrationPurchaseBasketExtended>) {
    Object.assign(this, o);
  }
}

interface AddressExtended {
  CountyId: number;
  CountryId: number;
}
@Component({
  selector: 'fgb-registration-payment',
  templateUrl: './registration-payment.component.html',
  styleUrls: ['./registration-payment.component.scss'],
})
export class RegistrationPaymentComponent implements OnInit {
  readonly stepType = StepType.Payment;
  basket$: Observable<RegistrationPurchaseBasket>;
  basketItems: RegistrationPurchaseBasketItem[] = [];
  providerResponse?: ProviderUiActionResponse;
  editingBillingAddress: boolean = false;
  editingShippingAddress: boolean = false;
  paymentInitialised: boolean = false;
  billingAddress: Address | undefined;
  shippingAddress: Address | undefined;
  billingAddressForm: UntypedFormGroup;
  shippingAddressForm: UntypedFormGroup;
  giftCodeInput: string = '';
  totalBasketPrice: number;
  totalItemPrice: number;
  totalTaxPrice: number;
  totalShippingPrice: number;
  appliedGiftCodes: GiftCodeModel[] = [];
  giftCodeInputMade = false;
  emptyBasket: RegistrationPurchaseBasket = {
    Tax: 0,
    TotalPrice: 0,
    TotalPriceWithTaxAndShipping: 0,
    TotalPriceWithTax: 0,
    TotalShipping: 0,
    TotalTax: 0,
    GiftCodes: [],
    BasketItems: [],
  };
  currentUser: MemberDetails | undefined;
  addresses: Address[] = [];
  cardTokenCount: number = 0;
  payWithCardToken: boolean = false;
  giftCodeAccepted: boolean = false;
  regions: any[];

  @ViewChild('paymentModal', { static: true }) paymentModalRef: ElementRef;
  @ViewChild('unappliedGiftCodeModal', { static: true }) unappliedGiftCodeModalRef: ElementRef;
  countryId: number = 0;
  regionId: number = 0;
  billingCountryId: number = 0;
  billingRegionId: number = 0;
  addressFormControl = new FormControl('', [Validators.required, FGBValidators.Empty]);
  private _refreshSubject: Subject<RegistrationBasketState | RegistrationPurchaseBasketExtended> = new Subject();

  constructor(
    private errorService: ErrorStateService,
    private registrationStepQuery: RegistrationStepQuery,
    private registrationStepService: RegistrationStepService,
    private registrationBasketService: RegistrationBasketService,
    private registrationBasketQuery: RegistrationBasketQuery,
    private logService: LoggingService,
    private paymentService: PaymentService,
    private giftCodeService: GiftCodeService,
    private formBuilder: UntypedFormBuilder,
    private memberQuery: MemberQuery,
    private memberService: MemberService,
    private addressQuery: AddressQuery,
    private addressService: AddressService,
    private cardTokenQuery: CardTokenQuery,
    private authService: AuthService,
    private modalService: NgbModal,
    private destroyRef: DestroyRef,
    private apiService: ApiService
  ) {}

  ngOnInit() {
    // Initialize forms
    this._initializeForms();

    this.logService.info('Retrieving latest basket information...');
    this.registrationBasketService.fetchRegistrationBasketForCurrentWizard().toPromise();

    this.registrationBasketQuery
      .select()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((basket) => {
        this._refreshSubject.next(basket);
      });

    this.basket$ = this.refresh$.pipe(
      tap((basket: RegistrationBasketState | RegistrationPurchaseBasketExtended) => {
        if (basket && basket.BasketItems?.length === 0) {
          this.registrationStepService.completeCurrentRegistration().toPromise();
        }
        if (this.appliedGiftCodes.length < 1 || basket instanceof RegistrationPurchaseBasketExtended) {
          this.basketItems = basket.BasketItems;
          this.totalBasketPrice = basket.TotalPriceWithTaxAndShipping;
          this.totalItemPrice = basket.TotalPrice;
          this.totalShippingPrice = basket.TotalShipping;
          this.totalTaxPrice = basket.TotalTax;
        }
      })
    ) as Observable<RegistrationPurchaseBasket>;

    const user = this.registrationStepQuery.getStep(StepType.PersonalDetails)?.Data as MemberDetailsRegistration;
    iif(
      () => this.authService.isAuthenticated() && !user.IsFirstTimeUser,
      this.memberService.fetchMemberDetails().pipe(
        switchMap(() =>
          this.memberQuery.selectMemberDetails().pipe(
            tap(() =>
              lastValueFrom(this.addressQuery.selectAddresses([AddressType.DefaultAddress])).then((addresses) => {
                this.addresses = addresses;
              })
            )
          )
        )
      ),
      of(user)
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((md) => {
        this.currentUser = md;
        this.billingAddress = this._getBillingAddress();
        this.shippingAddress = this._getShippingAddress();
        // re-initialize forms with data
        this._initializeForms(this.billingAddress, this.shippingAddress);
      });

    if (this.authService.isAuthenticated()) {
      this.cardTokenQuery
        .selectCard()
        .pipe(tap((tokens) => (this.cardTokenCount = tokens.length)))
        .toPromise();
    }
  }

  back(): void {
    this.registrationStepService.navigateToPreviousStep(this.stepType);
  }

  openPaymentModal(template: any): Promise<any> {
    return this.modalService.open(template, {
      centered: true,
      backdrop: 'static',
      windowClass: 'registration-payment-modal',
    }).result;
  }

  closeModal(): void {
    this.modalService.dismissAll();
  }

  submitStep() {
    this.errorService.clearErrors();
    this.shippingAddressForm.markAllAsTouched();
    this.billingAddressForm.markAllAsTouched();

    if (!this.shippingAddressForm.valid || !this.billingAddressForm.valid) {
      this.errorService.addError(
        `Please update your ${!this.billingAddressForm.valid ? 'billing' : ''} ${
          !this.shippingAddressForm.valid && !this.billingAddressForm.valid ? 'and' : ''
        } ${!this.shippingAddressForm.valid ? 'shipping' : ''} address`
      );
      return;
    }

    if (this.shippingAddressForm.valid && this.billingAddressForm.valid) {
      if (this.giftCodeInputMade && !this.errorService.hasErrors()) {
        this.unappliedGiftCodeModal();
      } else {
        this.detectGiftCodeInput(this.giftCodeInput);
        if (!this.paymentInitialised && this.billingAddress && this.shippingAddress) {
          this.paymentInitialised = true;
          this.logService.info('Retrieving provider action response for payment request...');
          const callbackUrl: string = document.getElementsByTagName('base')[0].href + '/register/payment/result';
          // Contact the API to get the provider action response
          firstValueFrom(
            this.registrationStepService.makeRegistrationPayment(
              this.billingAddress,
              this.shippingAddress,
              callbackUrl,
              this.appliedGiftCodes,
              this.payWithCardToken
            )
          )
            .then((response) => {
              if (response.ProviderActionInformation.ActionKind == ProviderActionKind.None) {
                this.registrationStepService.submitRegistrationStepData(this.stepType, this.emptyBasket).toPromise();
              } else {
                this.openPaymentModal(this.paymentModalRef).catch(() => (this.paymentInitialised = false));
                this.paymentService.process(response, 'paymentdiv', 'register/payment');
              }
            })
            .catch((error) => {
              this.logService.error(error);
              this.errorService.addError('An error occurred, please try again.');
              this.paymentInitialised = false;
            })
            .finally(() => (this.paymentInitialised = false));
        }
      }
    }
  }

  /** Gets the regions of the country */
  private onCountryChange(event: Country): void {
    this.regions = event?.Regions;
  }

  onBillingCountryChange(event: Country): void {
    this.billingCountryId = event?.ID;
    this.onCountryChange(event);
  }

  onShippingCountryChange(event: Country): void {
    this.countryId = event?.ID;
    this.onCountryChange(event);
  }

  onBillingRegionChange(event: string): void {
    this.billingRegionId = toNumber(this.regions?.find((r: Region) => r?.RegionName === event)?.RegionId);
  }

  onShippingRegionChange(event: string): void {
    this.regionId = toNumber(this.regions?.find((r: Region) => r?.RegionName === event)?.RegionId);
  }

  private _initializeForms(billing = {}, shipping = {}): void {
    this.billingAddressForm = this._generateBillingAddressForm(billing);
    this.shippingAddressForm = this._generateShippingAddressForm(shipping);
  }

  private _generateBillingAddressForm(billing: any): UntypedFormGroup {
    return this.formBuilder.group({
      Street: [billing.Street || '', [Validators.required, FGBValidators.Empty]],
      Town: [billing.Town || '', [Validators.required, FGBValidators.Empty]],
      County: [billing.County || '', [Validators.required, FGBValidators.Empty]],
      Postcode: [billing.Postcode || '', [Validators.required, FGBValidators.Empty]],
      Country: [billing.Country || '', [Validators.required, FGBValidators.Empty]],
      AddressType: [AddressType.BillingAddress1],
    });
  }

  private _generateShippingAddressForm(shipping: any): UntypedFormGroup {
    return this.formBuilder.group({
      Street: [shipping.Street || '', [Validators.required, FGBValidators.Empty]],
      Town: [shipping.Town || '', [Validators.required, FGBValidators.Empty]],
      County: [shipping.County || '', [Validators.required, FGBValidators.Empty]],
      Postcode: [shipping.Postcode || '', [Validators.required, FGBValidators.Empty]],
      Country: [shipping.Country || '', [Validators.required, FGBValidators.Empty]],
      AddressType: [AddressType.ShippingAddress1],
    });
  }

  /** Start editing the user's billing address. */
  editBillingAddress() {
    this.editingBillingAddress = true;
    this.logService.debug('Opening billing address form with initial value', this.billingAddress);
  }

  /** Start editing the user's shipping address. */
  editShippingAddress() {
    this.editingShippingAddress = true;
    this.logService.debug('Opening shipping address form with initial value', this.editingShippingAddress);
  }

  /* Cancel editing and revert back to initial value */
  cancelShippingForm(): void {
    this.editingShippingAddress = false;
    // re-initialize form with the initial value
    this.shippingAddressForm = this._generateShippingAddressForm(this.shippingAddress);
    this.addressFormControl.setValue('');
  }

  /* Cancel editing and revert back to initial value */
  cancelBillingForm(): void {
    this.editingBillingAddress = false;
    // re-initialize form with the initial value
    this.billingAddressForm = this._generateBillingAddressForm(this.billingAddress);
    this.addressFormControl.setValue('');
  }

  /** Update the user's billing address with the value of the billingAddressForm. */
  updateBillingAddress() {
    this.billingAddressForm.markAllAsTouched();
    this.addressFormControl.markAllAsTouched();

    if (this.billingAddressForm.valid && this.addressFormControl.valid) {
      this.billingAddress = {
        ...this.billingAddress,
        ...this.billingAddressForm.value,
        Street: this.billingAddressForm.value.Street + ' ' + this.addressFormControl.value,
        CountryId: this.billingCountryId,
        CountyId: this.billingRegionId,
      };

      this.editingBillingAddress = false;
      this.billingAddressForm.get('Street')?.patchValue(this.billingAddress?.Street);
      this.addressFormControl.patchValue('');

      if (this.authService.isAuthenticated() && this.memberQuery.getUserContext().status !== AccountStatus.FirstTimeUser) {
        this.addressService.saveAddress(this.billingAddress!);
      }
    }
  }

  async updateShippingAddress() {
    this.shippingAddressForm.markAllAsTouched();
    this.addressFormControl.markAllAsTouched();

    if (this.shippingAddressForm.valid && this.addressFormControl.valid) {
      this.shippingAddress = {
        ...this.shippingAddress,
        ...this.shippingAddressForm.value,
        Street: this.shippingAddressForm.value.Street + ' ' + this.addressFormControl.value,
        CountryId: this.countryId,
        CountyId: this.regionId,
      };

      this.editingShippingAddress = false;
      this.shippingAddressForm.get('Street')?.patchValue(this.shippingAddress?.Street);
      this.addressFormControl.patchValue('');

      if (this.authService.isAuthenticated() && this.memberQuery.getUserContext().status !== AccountStatus.FirstTimeUser) {
        this.addressService.saveAddress(this.shippingAddress!);
      }
      this.apiService
        .post('register/basket', {
          PaymentAddresses: [this.shippingAddress, this.billingAddress],
          AppliedGiftCodes: this.appliedGiftCodes,
        })
        .subscribe((basket: RegistrationPurchaseBasketExtended) =>
          this._refreshSubject.next(new RegistrationPurchaseBasketExtended(basket))
        );
    }
  }

  /** Gets the user's address from the personal details step data. */
  private _getDefaultBillingAddress(): (Address & AddressExtended) | undefined {
    const personalDetailsStep = this.registrationStepQuery.getStep(StepType.PersonalDetails);
    if (personalDetailsStep) {
      const personalDetails = personalDetailsStep.Data as MemberDetailsRegistration & AddressExtended;
      return {
        AddressName: 'Billing Address',
        FirstName: personalDetails.FirstName,
        Surname: personalDetails.Surname,
        Street: personalDetails.Street,
        Town: personalDetails.Town,
        County: personalDetails.County,
        Postcode: personalDetails.PostCode,
        Country: personalDetails.Country,
        HomeNumber: personalDetails.HomeNumber,
        EmailAddress: personalDetails.EmailAddress1,
        PortalId: personalDetails.PortalId,
        ClubId: personalDetails.ClubId,
        CountryId: personalDetails.CountryId,
        CountyId: personalDetails.CountyId,
      } as Address & AddressExtended;
    }
    if (this.addresses) {
      let billingAddress1 = this.addresses.filter((a) => a.AddressType === AddressType.BillingAddress1)[0] as Address &
        AddressExtended;
      if (billingAddress1) {
        return billingAddress1;
      }

      let billingAddress2 = this.addresses.filter((a) => a.AddressType === AddressType.BillingAddress2)[0] as Address &
        AddressExtended;
      if (billingAddress2) {
        return billingAddress2;
      }
    }
    if (this.currentUser) {
      return {
        AddressName: 'Billing Address',
        FirstName: this.currentUser.FirstName,
        Surname: this.currentUser.Surname,
        Street: this.currentUser.Street,
        Town: this.currentUser.Town,
        County: this.currentUser.County,
        Postcode: this.currentUser.PostCode,
        Country: this.currentUser.Country,
        HomeNumber: this.currentUser.HomeNumber,
        EmailAddress: this.currentUser.EmailAddress1,
        PortalId: this.currentUser.PortalId,
        ClubId: this.currentUser.ClubId,
        CountryId: this.currentUser.CountryId,
        CountyId: this.currentUser.CountyId,
      } as Address & AddressExtended;
    }
    return;
  }

  private _getDefaultShippingAddress(): (Address & AddressExtended) | undefined {
    const personalDetailsStep = this.registrationStepQuery.getStep(StepType.PersonalDetails);
    if (personalDetailsStep) {
      const personalDetails = personalDetailsStep.Data as MemberDetailsRegistration & AddressExtended;
      return {
        AddressName: 'Shipping Address',
        FirstName: personalDetails.FirstName,
        Surname: personalDetails.Surname,
        Street: personalDetails.Street,
        Town: personalDetails.Town,
        County: personalDetails.County,
        Postcode: personalDetails.PostCode,
        Country: personalDetails.Country,
        HomeNumber: personalDetails.HomeNumber,
        EmailAddress: personalDetails.EmailAddress1,
        PortalId: personalDetails.PortalId,
        ClubId: personalDetails.ClubId,
        CountryId: personalDetails.CountryId,
        CountyId: personalDetails.CountyId,
      } as Address & AddressExtended;
    }
    if (this.addresses) {
      let shippingAddress1 = this.addresses.filter((a) => a.AddressType === AddressType.ShippingAddress1)[0] as Address &
        AddressExtended;
      if (shippingAddress1) {
        return shippingAddress1;
      }

      let shippingAddress2 = this.addresses.filter((a) => a.AddressType === AddressType.ShippingAddress2)[0] as Address &
        AddressExtended;
      if (shippingAddress2) {
        return shippingAddress2;
      }
    }
    if (this.currentUser) {
      return {
        AddressName: 'Billing Address',
        FirstName: this.currentUser.FirstName,
        Surname: this.currentUser.Surname,
        Street: this.currentUser.Street,
        Town: this.currentUser.Town,
        County: this.currentUser.County,
        Postcode: this.currentUser.PostCode,
        Country: this.currentUser.Country,
        HomeNumber: this.currentUser.HomeNumber,
        EmailAddress: this.currentUser.EmailAddress1,
        PortalId: this.currentUser.PortalId,
        ClubId: this.currentUser.ClubId,
        CountryId: this.currentUser.CountryId,
        CountyId: this.currentUser.CountyId,
      } as Address & AddressExtended;
    }
    return;
  }

  /** Gets the user's billing address from the session storage, or the personal details step if not found. */
  private _getBillingAddress(): (Address & AddressExtended) | undefined {
    try {
      const billingAddressFromSession = sessionStorage.getItem('BillingAddress');
      if (billingAddressFromSession) {
        this.logService.info('Using billing address from session storage');
        return JSON.parse(billingAddressFromSession);
      }
    } catch {
      this.logService.error('Failed to retrieve billing address from session storage, using default address instead...');
    }

    return this._getDefaultBillingAddress();
  }

  /** Gets the user's shipping address from the shipping address step data, or the personal details step if not provided. */
  private _getShippingAddress(): (Address & AddressExtended) | undefined {
    const shippingAddressStep = this.registrationStepQuery.getStep(StepType.ShippingAddress);
    if (shippingAddressStep && shippingAddressStep.Data) {
      return shippingAddressStep.Data as Address & AddressExtended;
    }

    return this._getDefaultShippingAddress();
  }

  /** Takes in a gift code string, checks to see if the code is valid and returns the calculated discount */
  applyGiftCode() {
    this.errorService.clearErrors();
    this.giftCodeAccepted = false;

    if (!this.giftCodeInput) {
      this.errorService.addError('Gift Code cannot be empty');
      return;
    }

    if (!!this.appliedGiftCodes.filter((code) => code.Code === this.giftCodeInput).length) {
      this.errorService.addError('This gift code has already been applied');
      return;
    }

    if (this.totalBasketPrice === 0) {
      this.errorService.addError('Gift code cannot be applied if the basket value is 0');
      return;
    }

    if (this.appliedGiftCodes.length > 2) {
      this.errorService.addError('The maximum amount of gift codes have been applied');
      return;
    }

    firstValueFrom(this.giftCodeService.validateGiftCode(this.giftCodeInput))
      .then((giftCode) => {
        if (
          giftCode.GiftCodeAssignType == GiftCodeType.Product &&
          !this.basketItems.filter(
            (item) =>
              item.MembershipOption.ProductId == giftCode.EntitlementItemId ||
              item.MembershipOption.ProductId == giftCode.EntitlementItemId2
          ).length
        ) {
          this.errorService.addError('Gift code does not apply to any of the basket items.');
          return;
        } else if (
          giftCode.EntitlementValueType == GiftCodeEntitlementValueType.Percentage &&
          !!this.appliedGiftCodes.filter((gc) => gc.EntitlementValueType == GiftCodeEntitlementValueType.Percentage).length
        ) {
          this.errorService.addError('Only one percentage discount gift code can applied.');
          return;
        } else if (giftCode.GiftCodeStatus != 0) {
          this.errorService.addError('Gift code status not avalaible.');
          return;
        } else {
          this.giftCodeAccepted = true;
          this.giftCodeInput = '';
          this.giftCodeInputMade = false;
        }

        if (this.appliedGiftCodes.length > 0) {
          /** reset basket values and recalculate with added giftcodes */
          this.resetBasketValues();
        }

        if (giftCode.EntitlementValueType === GiftCodeEntitlementValueType.Value) {
          this.appliedGiftCodes.unshift(giftCode);
        } else {
          this.appliedGiftCodes.push(giftCode);
        }

        this.appliedGiftCodes.forEach((x) => {
          this._calculateGiftCodeDeduction(x);
        });
      })
      .catch((err) => this.removeUnappliedGiftCode());
  }

  /** Calculate the new total basket price after Gift Code has been applied */
  private _calculateGiftCodeDeduction(giftCode: GiftCodeModel) {
    this.errorService.clearErrors();
    let discount = 0;

    if (giftCode.GiftCodeAssignType === GiftCodeType.Order) {
      switch (giftCode.EntitlementValueType) {
        // Value
        case GiftCodeEntitlementValueType.Value: {
          discount = giftCode.EntitlementValue;
          break;
        }
        case GiftCodeEntitlementValueType.Percentage: {
          // Percentage
          discount = (giftCode.EntitlementValue / 100) * this.totalBasketPrice;
          break;
        }
        case GiftCodeEntitlementValueType.NumberOfEntitlements: {
          // Number of Items - Only used for Free
          this.appliedGiftCodes = this.appliedGiftCodes.filter((x) => x.Code === giftCode.Code);
          discount = this.totalBasketPrice;
          break;
        }
        default: {
          this.errorService.addError('Invalid Gift Code');
          break;
        }
      }
    } else if (giftCode.GiftCodeAssignType === GiftCodeType.Shipping) {
      if (this.totalShippingPrice === 0) {
        this.appliedGiftCodes = this.appliedGiftCodes.filter((x) => x.Code !== giftCode.Code);
        this.errorService.addError('Gift Code cannot be applied if the shipping is already 0');
        return;
      }

      switch (giftCode.EntitlementValueType) {
        case GiftCodeEntitlementValueType.Value: {
          // Value
          discount = giftCode.EntitlementValue;
          break;
        }
        case GiftCodeEntitlementValueType.Percentage: {
          // Percentage
          discount = (giftCode.EntitlementValue / 100) * this.totalShippingPrice;
          break;
        }
        case GiftCodeEntitlementValueType.NumberOfEntitlements: {
          // Number of Items - Only used for Free
          discount = this.totalShippingPrice;
          break;
        }
        default: {
          this.errorService.addError('Invalid Gift Code');
          break;
        }
      }

      this.totalShippingPrice = Math.max(0, this.totalShippingPrice - discount);
    } else if (giftCode.GiftCodeAssignType == GiftCodeType.Product) {
      var numberOfTimesApplied = 0;
      this.basketItems
        .filter(
          (item) =>
            item.MembershipOption.ProductId == giftCode.EntitlementItemId ||
            item.MembershipOption.ProductId == giftCode.EntitlementItemId2
        )
        .forEach((item) => {
          if (numberOfTimesApplied < giftCode.EntitlementValue) {
            numberOfTimesApplied++;
            discount += item.PriceWithTaxAndShipping;
          }
        });

      this.totalItemPrice = Math.max(0, this.totalItemPrice - discount);
    }
    this.totalBasketPrice = Math.max(0, this.totalBasketPrice - discount);
  }

  resetBasketValues() {
    this.registrationBasketQuery
      .select()
      .pipe(
        tap((basket) => {
          this.totalBasketPrice = basket.TotalPriceWithTaxAndShipping;
          this.totalItemPrice = basket.TotalPrice;
          this.totalShippingPrice = basket.TotalShipping;
          this.totalTaxPrice = basket.TotalTax;
        })
      )
      .toPromise();
  }

  /** Works out the total basket price after removal of gift code */
  removeGiftCode(code: string) {
    this.errorService.clearErrors();
    let giftCode = this.appliedGiftCodes.find((x) => x.Code === code);

    if (giftCode) {
      this.appliedGiftCodes = this.appliedGiftCodes.filter((x) => x !== giftCode);

      /** reset basket values and recalculate with remaining giftcodes */
      this.resetBasketValues();

      if (this.appliedGiftCodes.length > 0) {
        this.appliedGiftCodes.forEach((x) => {
          this._calculateGiftCodeDeduction(x);
        });
      }
    }
  }

  detectGiftCodeInput(input: string) {
    this.giftCodeInputMade = false;
    if (/\S/.test(input)) {
      this.errorService.clearErrors();
      this.giftCodeInputMade = true;
    }
  }

  unappliedGiftCodeModal() {
    this.modalService.open(this.unappliedGiftCodeModalRef, {
      centered: true,
      windowClass: 'registration-payment-unapplied-giftcode-modal',
    });
  }

  removeUnappliedGiftCode() {
    this.giftCodeInput = '';
    this.giftCodeInputMade = false;
  }

  get refresh$() {
    return this._refreshSubject.asObservable();
  }
}
